
import React, { useMemo, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectGroup, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Poll, PollStatus } from '@/types/poll';
import useCategories from '@/hooks/useCategories';
import { Category } from '@/hooks/useCategories';

// Update the interface to include categories and isEditing
interface PollCategoryStatusFieldsProps {
  currentPoll: Poll;
  updateField: (field: keyof Poll, value: any) => void;
  categories?: Category[];
  isEditing?: boolean;
}

const PollCategoryStatusFields: React.FC<PollCategoryStatusFieldsProps> = ({ 
  currentPoll, 
  updateField, 
  categories: propCategories,
  isEditing 
}) => {
  // Use either provided categories from props or fetch them
  const { categories: fetchedCategories, isLoading } = useCategories(true);
  const categories = propCategories || fetchedCategories;
  
  console.log('PollCategoryStatusFields rendering:', {
    pollId: currentPoll?.id,
    category: currentPoll?.category,
    category_id: currentPoll?.category_id,
    categoriesLoaded: categories.length,
    isLoading
  });
  
  // Get the current category name for display in the SelectValue
  const selectedCategoryName = useMemo(() => {
    if (!currentPoll.category_id) return '';
    const category = categories.find(cat => cat.id === currentPoll.category_id);
    console.log('Selected category lookup:', { 
      looking_for: currentPoll.category_id,
      found: category ? true : false,
      categoryName: category?.name
    });
    return category ? category.name : '';
  }, [currentPoll.category_id, categories]);
  
  // Update category_id and category together for consistency
  const handleCategoryChange = (value: string) => {
    // Find the selected category object
    const selectedCategory = categories.find(cat => cat.id === value);
    console.log('Category selected:', value);
    console.log('Selected category object:', selectedCategory);
    
    if (selectedCategory) {
      // Important: Update both values simultaneously to avoid sync issues
      // First update category to prevent the sync effect from overriding
      console.log('Setting both category and category_id in handleCategoryChange');
      updateField('category', selectedCategory.name);
      updateField('category_id', value);
    } else {
      // If no category found, just update the ID
      updateField('category_id', value);
    }
  };
  
  // Initial sync only - run only once when categories load
  useEffect(() => {
    if (categories.length > 0 && currentPoll.category_id && !currentPoll.category) {
      const selectedCategory = categories.find(cat => cat.id === currentPoll.category_id);
      
      if (selectedCategory) {
        console.log('Initial sync: Setting category name from ID:', selectedCategory.name);
        updateField('category', selectedCategory.name);
      }
    } else if (categories.length > 0 && currentPoll.category && !currentPoll.category_id) {
      const selectedCategory = categories.find(cat => cat.name === currentPoll.category);
      
      if (selectedCategory) {
        console.log('Initial sync: Setting category ID from name:', selectedCategory.id);
        updateField('category_id', selectedCategory.id);
      }
    }
  // Run this effect only once when categories are loaded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length > 0, currentPoll.category_id, currentPoll.category]);
  
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="category_id">Category</Label>
        <Select 
          value={currentPoll.category_id || undefined}
          onValueChange={handleCategoryChange}
          disabled={isLoading}
        >
          <SelectTrigger id="category_id" className="bg-background">
            <SelectValue placeholder="Select category">
              {selectedCategoryName || "Select category"}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-gray-800 z-50">
            <SelectGroup>
              {isLoading ? (
                <SelectItem value="loading" disabled>Loading categories...</SelectItem>
              ) : categories.length > 0 ? (
                // Sort categories alphabetically by name
                [...categories]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))
              ) : (
                <SelectItem value="no-categories" disabled>No categories available</SelectItem>
              )}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Select 
          value={currentPoll.status} 
          onValueChange={(value) => updateField('status', value as PollStatus)}
        >
          <SelectTrigger id="status" className="bg-background">
            <SelectValue>
              {currentPoll.status === 'draft' ? 'Draft' : 
               currentPoll.status === 'live' ? 'Active' : 
               currentPoll.status === 'closed' ? 'Closed' : 'Select status'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-gray-800 z-50">
            <SelectGroup>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="live">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default PollCategoryStatusFields;
